import React, { useState } from 'react'
import {Sparkles, TrendingUp, AlertCircle, CheckCircle} from 'lucide-react'
import { lumi } from '@/lib/lumi'

interface Recommendation {
  rank: number
  material: string
  suitability: string
  lifespan: string
  costRange: string
  benefits: string[]
  drawbacks: string[]
}

interface RecommendationResult {
  recommendations: Recommendation[]
  climateConsiderations?: string
  budgetAnalysis?: string
  rawResponse?: string
  error?: string
}

interface AIRecommenderProps {
  zipCode: string
  budget: number
  onSelectMaterial?: (material: string) => void
}

export default function AIRecommender({ zipCode, budget, onSelectMaterial }: AIRecommenderProps) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<RecommendationResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [climate, setClimate] = useState('')
  const [homeStyle, setHomeStyle] = useState('')
  const [priorities, setPriorities] = useState<string[]>([])

  const priorityOptions = [
    'Durability',
    'Cost-effective',
    'Energy efficiency',
    'Low maintenance',
    'Eco-friendly',
    'Aesthetic appeal',
    'Weather resistance'
  ]

  const togglePriority = (priority: string) => {
    setPriorities(prev =>
      prev.includes(priority)
        ? prev.filter(p => p !== priority)
        : [...prev, priority]
    )
  }

  const handleGetRecommendations = async () => {
    if (!zipCode || !budget || budget <= 0) {
      setError('Please enter a valid ZIP code and budget in the calculator above first')
      return
    }

    setLoading(true)
    setError(null)
    setResult(null)

    try {
      const response = await lumi.functions.invoke('recommendMaterial', {
        method: 'POST',
        body: {
          zipCode,
          budget,
          climate: climate || undefined,
          homeStyle: homeStyle || undefined,
          priorities: priorities.length > 0 ? priorities : undefined
        }
      })

      setResult(response as RecommendationResult)
    } catch (err: any) {
      const errorMessage = err?.data?.error || 'Failed to get AI recommendations. Please try again.'
      setError(errorMessage)
      console.error('AI Recommender error:', err)
    } finally {
      setLoading(false)
    }
  }

  const mapMaterialToCalculator = (aiMaterial: string): string => {
    const materialMap: Record<string, string> = {
      'architectural asphalt': 'asphalt',
      'asphalt': 'asphalt',
      'metal': 'metal',
      'standing seam': 'metal',
      'tile': 'tile',
      'clay tile': 'tile',
      'slate': 'slate',
      'wood shake': 'wood',
      'solar': 'solar',
      'green roof': 'green',
      'recycled': 'recycled_shingles',
      'composite': 'composite'
    }

    const lowerMaterial = aiMaterial.toLowerCase()
    for (const [key, value] of Object.entries(materialMap)) {
      if (lowerMaterial.includes(key)) {
        return value
      }
    }
    return 'asphalt'
  }

  return (
    <div className="mt-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-xl p-6 border-2 border-purple-200">
      <div className="flex items-center mb-4">
        <div className="bg-purple-600 p-2 rounded-full mr-3">
          <Sparkles className="text-white" size={24} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-gray-900">AI Material Recommender</h3>
          <p className="text-sm text-gray-600">Get personalized recommendations based on your climate and budget</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Climate Description (Optional)
          </label>
          <input
            type="text"
            value={climate}
            onChange={(e) => setClimate(e.target.value)}
            placeholder="e.g., hot and humid, cold and snowy"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Home Style (Optional)
          </label>
          <input
            type="text"
            value={homeStyle}
            onChange={(e) => setHomeStyle(e.target.value)}
            placeholder="e.g., modern, colonial, ranch"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Your Priorities (Optional)
          </label>
          <div className="flex flex-wrap gap-2">
            {priorityOptions.map((priority) => (
              <button
                key={priority}
                type="button"
                onClick={() => togglePriority(priority)}
                className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                  priorities.includes(priority)
                    ? 'bg-purple-600 text-white'
                    : 'bg-white text-gray-700 border border-gray-300 hover:border-purple-400'
                }`}
              >
                {priority}
              </button>
            ))}
          </div>
        </div>

        <button
          type="button"
          onClick={handleGetRecommendations}
          disabled={loading || !zipCode || !budget}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-bold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
        >
          {loading ? (
            <>
              <TrendingUp className="animate-pulse" size={20} />
              <span>AI Analyzing...</span>
            </>
          ) : (
            <>
              <Sparkles size={20} />
              <span>Get AI Recommendations</span>
            </>
          )}
        </button>
      </div>

      {error && (
        <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start text-sm text-red-800">
          <AlertCircle size={20} className="mr-2 flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {result && (
        <div className="mt-6 space-y-4">
          {result.climateConsiderations && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-semibold text-blue-900 mb-2">Climate Analysis</h4>
              <p className="text-sm text-blue-800">{result.climateConsiderations}</p>
            </div>
          )}

          {result.budgetAnalysis && (
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
              <h4 className="font-semibold text-green-900 mb-2">Budget Analysis</h4>
              <p className="text-sm text-green-800">{result.budgetAnalysis}</p>
            </div>
          )}

          {result.recommendations && result.recommendations.length > 0 && (
            <div className="space-y-3">
              <h4 className="font-bold text-gray-900 text-lg">Top Recommendations</h4>
              {result.recommendations.map((rec) => (
                <div
                  key={rec.rank}
                  className="bg-white border-2 border-purple-200 rounded-lg p-4 hover:shadow-lg transition-shadow"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="bg-purple-600 text-white font-bold rounded-full w-8 h-8 flex items-center justify-center">
                        {rec.rank}
                      </span>
                      <h5 className="font-bold text-lg text-gray-900">{rec.material}</h5>
                    </div>
                    {onSelectMaterial && (
                      <button
                        type="button"
                        onClick={() => onSelectMaterial(mapMaterialToCalculator(rec.material))}
                        className="bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold px-3 py-1 rounded-full transition-colors"
                      >
                        Use This
                      </button>
                    )}
                  </div>

                  <div className="space-y-2 text-sm">
                    <p className="text-gray-700">{rec.suitability}</p>
                    
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div className="bg-gray-50 p-2 rounded">
                        <span className="font-semibold">Lifespan:</span> {rec.lifespan}
                      </div>
                      <div className="bg-gray-50 p-2 rounded">
                        <span className="font-semibold">Cost:</span> {rec.costRange}
                      </div>
                    </div>

                    <div>
                      <span className="font-semibold text-green-700">Benefits:</span>
                      <ul className="list-disc list-inside text-gray-700 ml-2">
                        {rec.benefits?.map((benefit, idx) => (
                          <li key={idx}>{benefit}</li>
                        ))}
                      </ul>
                    </div>

                    {rec.drawbacks && rec.drawbacks.length > 0 && (
                      <div>
                        <span className="font-semibold text-orange-700">Considerations:</span>
                        <ul className="list-disc list-inside text-gray-700 ml-2">
                          {rec.drawbacks.map((drawback, idx) => (
                            <li key={idx}>{drawback}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {result.rawResponse && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 whitespace-pre-wrap">{result.rawResponse}</p>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
